// ═══════════════════════════════════════════════════════════════════════════════
// Employer — create an inventory (stocktake) task instance
// ═══════════════════════════════════════════════════════════════════════════════

import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../../contexts/LanguageContext';
import { useAuth } from '../../contexts/AuthContext';
import { useToast } from '../../components/ui/ToastProvider';
import { getHelperTodayYmd } from '../../lib/userTodayYmd';
import {
  LIMIT_ERROR_CODE,
  MONTHLY_KIND,
  RECURRENCE_TYPE,
  USER_ROLE,
  type MonthlyKind,
  type RecurrenceType,
} from '@fdh/shared';
import { ApiClientError, inventoryApi } from '../../lib/api';
import { emitInventoryMetadataLimitIfReached, isLimitReachedError } from '../../lib/limitError';
import { mapApiClientErrorToMessage } from '../../i18n/mapApiClientError';
import {
  MODAL_BACKDROP_CENTER_CLASS,
  MODAL_BACKDROP_SCRIM_CLASS,
  MODAL_PANEL_SCROLL_DVH_CLASS,
} from '../../lib/modalClasses';
import { AppButton } from '../../components/ui/AppButton';
import { HelperDueReminderCheckbox } from '../../components/employer/HelperDueReminderCheckbox';
import {
  emptyMonthlySchedulePatch,
  TaskMonthlyScheduleControls,
} from '../../components/employer/TaskMonthlyScheduleControls';
import { InventoryChecklistItemRow } from '../../components/employer/InventoryChecklistItemRow';
import {
  buildInventoryWizardReviewRows,
  InventoryWizardReviewStep,
} from '../../components/employer/InventoryWizardReviewStep';
import { ConfirmDialog } from '../../components/ui/ConfirmDialog';
import { IsoDatePicker } from '../../components/IsoDatePicker';
import { NativeTimeInput } from '../../components/NativeTimeInput';
import {
  buildInventoryCategorySuggestions,
  buildInventoryLocationSuggestions,
  buildInventoryMetadataSuggestionLabelMap,
  mergeInventoryMetadataSuggestionChips,
  normalizeEmployerContentLanguage,
} from '../../lib/inventoryTaskMetadataSuggestions';

const WIZARD_TOTAL_STEPS = 6;
const EXPIRY_LEAD_OPTIONS = [3, 7, 10, 14, 30] as const;
const WIZARD_STEP_TITLE_KEYS = [
  'task.inventory.wizard.step1Title',
  'task.inventory.wizard.step2Title',
  'task.inventory.wizard.step3Title',
  'task.inventory.wizard.step4Title',
  'task.inventory.wizard.step5Title',
  'task.inventory.wizard.step7Title',
] as const;

const REC_DAYS = [0, 1, 2, 3, 4, 5, 6] as const;
const REC_DAY_KEYS = [
  'task.day.sun',
  'task.day.mon',
  'task.day.tue',
  'task.day.wed',
  'task.day.thu',
  'task.day.fri',
  'task.day.sat',
] as const;

type ExclRange = { startDate: string; endDate: string };

export const EmployerInventoryNewTaskPage = () => {
  const { t, language: uiLanguage } = useLanguage();
  const { user } = useAuth();
  const { showSuccessToast } = useToast();
  const navigate = useNavigate();

  const [lists, setLists] = useState<
    { id: string; name: string; scheduleType?: string; scheduleWeekday?: number | null; itemCount: number }[]
  >([]);
  const [items, setItems] = useState<{ id: string; name: string }[]>([]);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  const [selectedListId, setSelectedListId] = useState('');
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [recurrence, setRecurrence] = useState<RecurrenceType>(RECURRENCE_TYPE.NONE);
  const [recurrenceDays, setRecurrenceDays] = useState<number[]>([]);
  const [monthlyKind, setMonthlyKind] = useState<MonthlyKind | null>(null);
  const [recurrenceMonthDays, setRecurrenceMonthDays] = useState<number[]>([]);
  const [recurrenceOrdinal, setRecurrenceOrdinal] = useState<number | null>(null);
  const [occurrenceDate, setOccurrenceDate] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [exclusionRanges, setExclusionRanges] = useState<ExclRange[]>([]);
  const [dueTime, setDueTime] = useState('');
  const [notifyEmployer, setNotifyEmployer] = useState(true);
  const [remindHelperBeforeDue, setRemindHelperBeforeDue] = useState(false);
  const [notifyExpiryLeadDays, setNotifyExpiryLeadDays] = useState<number[]>([]);
  const [wizardStep, setWizardStep] = useState(1);
  const [categories, setCategories] = useState<string[]>([]);
  const [locations, setLocations] = useState<string[]>([]);
  const [categoryInput, setCategoryInput] = useState('');
  const [locationInput, setLocationInput] = useState('');
  const [suggestCategories, setSuggestCategories] = useState<string[]>([]);
  const [suggestLocations, setSuggestLocations] = useState<string[]>([]);
  const [maxInventoryLocations, setMaxInventoryLocations] = useState<number | null>(null);
  const [maxInventoryCategories, setMaxInventoryCategories] = useState<number | null>(null);
  const employerContentLanguage = useMemo(
    () => normalizeEmployerContentLanguage(user?.settings?.language, uiLanguage),
    [user?.settings?.language, uiLanguage],
  );
  const defaultLocationSuggestions = useMemo(
    () => buildInventoryLocationSuggestions(employerContentLanguage),
    [employerContentLanguage],
  );
  const defaultCategorySuggestions = useMemo(
    () => buildInventoryCategorySuggestions(employerContentLanguage),
    [employerContentLanguage],
  );
  const locationSuggestionLabelMap = useMemo(
    () => buildInventoryMetadataSuggestionLabelMap(defaultLocationSuggestions),
    [defaultLocationSuggestions],
  );
  const categorySuggestionLabelMap = useMemo(
    () => buildInventoryMetadataSuggestionLabelMap(defaultCategorySuggestions),
    [defaultCategorySuggestions],
  );

  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  const [createListOpen, setCreateListOpen] = useState(false);
  const [newListName, setNewListName] = useState('');
  const [editingListName, setEditingListName] = useState('');
  const [renameListModalOpen, setRenameListModalOpen] = useState(false);
  const [creatingList, setCreatingList] = useState(false);

  const [newItemName, setNewItemName] = useState('');
  const [addingItem, setAddingItem] = useState(false);
  const [removingItemId, setRemovingItemId] = useState<string | null>(null);
  const [editListModalOpen, setEditListModalOpen] = useState(false);
  const [removeListConfirmOpen, setRemoveListConfirmOpen] = useState(false);
  const [removeListLoading, setRemoveListLoading] = useState(false);
  const [affectedActiveTasks, setAffectedActiveTasks] = useState<{ taskId: string; title: string }[]>([]);

  const [listItemSelection, setListItemSelection] = useState<Record<string, boolean>>({});
  const [savingListItems, setSavingListItems] = useState(false);
  const [, setListLines] = useState<{ itemId: string; itemName: string; sortOrder: number }[]>([]);
  const [hiddenListItemIds, setHiddenListItemIds] = useState<string[]>([]);
  const [hiddenItemIdsInOtherLists, setHiddenItemIdsInOtherLists] = useState<string[]>([]);
  const [listLinesLoading, setListLinesLoading] = useState(false);
  const listLinesVersionRef = useRef(0);
  const itemsRef = useRef(items);
  useEffect(() => {
    itemsRef.current = items;
  }, [items]);

  const todayYmd = useMemo(
    () => getHelperTodayYmd(user?.settings?.timezone),
    [user?.settings?.timezone],
  );
  const resolvePageErrorMessage = (
    fallbackMessage: string,
    options?: {
      apiErrorMessage?: string;
      caught?: unknown;
    },
  ): string => {
    const apiErrorMessage = options?.apiErrorMessage?.trim();
    if (apiErrorMessage) {
      return apiErrorMessage;
    }
    const caught = options?.caught;
    if (caught instanceof ApiClientError) {
      return mapApiClientErrorToMessage(caught, t) ?? caught.message ?? fallbackMessage;
    }
    if (caught instanceof Error && caught.message.trim() !== '') {
      return caught.message;
    }
    return fallbackMessage;
  };

  const loadData = useCallback(async () => {
    setLoadError(null);
    setLoading(true);
    try {
      const [lr, ir] = await Promise.all([inventoryApi.listLists(), inventoryApi.listItems()]);
      if (lr.success && lr.data) {
        const listData = lr.data;
        setLists(listData);
        setSelectedListId((prev) => {
          if (prev && listData.some((x) => x.id === prev)) {
            return prev;
          }
          return listData[0]?.id ?? '';
        });
      } else {
        setLoadError(lr.error ?? t('task.inventory.loadError'));
      }
      if (ir.success && ir.data) {
        setItems(ir.data.map((x) => ({ id: x.id, name: x.name })));
      }
    } catch {
      setLoadError(t('task.inventory.loadError'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    void loadData();
  }, [loadData]);

  useEffect(() => {
    if (occurrenceDate === '' && todayYmd !== '') {
      setOccurrenceDate(todayYmd);
    }
  }, [occurrenceDate, todayYmd]);

  useEffect(() => {
    if (loading) {
      return;
    }
    void (async () => {
      const r = await inventoryApi.getTaskMetadataSuggestions();
      if (r.success && r.data) {
        setMaxInventoryLocations(r.data.maxInventoryLocations ?? null);
        setMaxInventoryCategories(r.data.maxInventoryCategories ?? null);
        setSuggestLocations(mergeInventoryMetadataSuggestionChips(defaultLocationSuggestions, r.data.locations));
        setSuggestCategories(mergeInventoryMetadataSuggestionChips(defaultCategorySuggestions, r.data.categories));
        return;
      }
      setSuggestLocations(defaultLocationSuggestions.map((row) => row.value));
      setSuggestCategories(defaultCategorySuggestions.map((row) => row.value));
    })();
  }, [defaultCategorySuggestions, defaultLocationSuggestions, loading]);

  useEffect(() => {
    if (wizardStep !== 6) {
      setCreateListOpen(false);
      setEditListModalOpen(false);
      setRenameListModalOpen(false);
      setRemoveListConfirmOpen(false);
    }
  }, [wizardStep]);

  const selectedList = useMemo(
    () => lists.find((l) => l.id === selectedListId) ?? null,
    [lists, selectedListId],
  );

  useEffect(() => {
    setEditingListName(selectedList?.name ?? '');
  }, [selectedList?.name]);

  const hiddenListItemIdSet = useMemo(() => new Set(hiddenListItemIds), [hiddenListItemIds]);
  const hiddenItemIdsInOtherListSet = useMemo(() => new Set(hiddenItemIdsInOtherLists), [hiddenItemIdsInOtherLists]);
  const shouldApplyOtherListHiddenFilter = Boolean(selectedList && selectedList.itemCount === 0);

  const visibleChecklistItems = useMemo(
    () =>
      items.filter((it) => {
        if (hiddenListItemIdSet.has(it.id)) {
          return false;
        }
        if (shouldApplyOtherListHiddenFilter && hiddenItemIdsInOtherListSet.has(it.id)) {
          return false;
        }
        return true;
      }),
    [hiddenItemIdsInOtherListSet, hiddenListItemIdSet, items, shouldApplyOtherListHiddenFilter],
  );

  const applySelectionFromServerLines = useCallback(
    (lines: { itemId: string; itemName: string; sortOrder: number }[]) => {
      const lineIds = new Set(lines.map((l) => l.itemId));
      const itList = itemsRef.current;
      const next: Record<string, boolean> = {};
      for (const it of itList) {
        next[it.id] = lineIds.has(it.id);
      }
      setListItemSelection(next);
    },
    [],
  );

  const fetchListLines = useCallback(
    async (listId: string) => {
      if (listId.trim() === '') {
        setListLines([]);
        setHiddenListItemIds([]);
        applySelectionFromServerLines([]);
        return;
      }
      const v = listLinesVersionRef.current + 1;
      listLinesVersionRef.current = v;
      setListLinesLoading(true);
      try {
        const r = await inventoryApi.getListLines(listId);
        if (listLinesVersionRef.current !== v) {
          return;
        }
        if (r.success && r.data?.items) {
          setListLines(r.data.items);
          setHiddenListItemIds(Array.isArray(r.data.hiddenItemIds) ? r.data.hiddenItemIds : []);
          applySelectionFromServerLines(r.data.items);
          return;
        }
        setListLines([]);
        setHiddenListItemIds([]);
        applySelectionFromServerLines([]);
      } catch {
        if (listLinesVersionRef.current === v) {
          setListLines([]);
          setHiddenListItemIds([]);
          applySelectionFromServerLines([]);
        }
      } finally {
        if (listLinesVersionRef.current === v) {
          setListLinesLoading(false);
        }
      }
    },
    [applySelectionFromServerLines],
  );

  useEffect(() => {
    void fetchListLines(selectedListId);
  }, [selectedListId, fetchListLines]);

  const syncHiddenItemIdsInOtherLists = useCallback(async () => {
    if (lists.length === 0) {
      setHiddenItemIdsInOtherLists([]);
      return;
    }
    const otherListIds = lists
      .map((row) => row.id)
      .filter((listId) => listId !== selectedListId);
    if (otherListIds.length === 0) {
      setHiddenItemIdsInOtherLists([]);
      return;
    }
    const results = await Promise.all(otherListIds.map((listId) => inventoryApi.getListLines(listId)));
    const hiddenIds = new Set<string>();
    for (const result of results) {
      if (!result.success || !result.data?.hiddenItemIds) {
        continue;
      }
      for (const hiddenId of result.data.hiddenItemIds) {
        hiddenIds.add(hiddenId);
      }
    }
    setHiddenItemIdsInOtherLists([...hiddenIds]);
  }, [lists, selectedListId]);

  useEffect(() => {
    void syncHiddenItemIdsInOtherLists();
  }, [syncHiddenItemIdsInOtherLists]);

  if (!user || user.role !== USER_ROLE.EMPLOYER) {
    return (
      <div className="flex min-h-full items-center justify-center bg-gray-50 px-4 dark:bg-gray-950">
        <p className="text-center text-gray-600 dark:text-gray-300" role="alert">
          {t('task.inventory.employerOnly')}
        </p>
      </div>
    );
  }

  const handleBack = () => {
    navigate('/tasks');
  };

  const handleWizardPrev = () => {
    setFormError(null);
    setWizardStep((s) => Math.max(1, s - 1));
  };

  const handleAddCategoryChip = (raw: string) => {
    const v = raw.trim();
    if (v === '') {
      return;
    }
    if (
      emitInventoryMetadataLimitIfReached(
        LIMIT_ERROR_CODE.INVENTORY_CATEGORY_LIMIT_REACHED,
        categories.length,
        maxInventoryCategories,
      )
    ) {
      return;
    }
    setCategories((prev) => (prev.includes(v) ? prev : [...prev, v]));
    setCategoryInput('');
  };

  const handleAddLocationChip = (raw: string) => {
    const v = raw.trim();
    if (v === '') {
      return;
    }
    if (
      emitInventoryMetadataLimitIfReached(
        LIMIT_ERROR_CODE.INVENTORY_LOCATION_LIMIT_REACHED,
        locations.length,
        maxInventoryLocations,
      )
    ) {
      return;
    }
    setLocations((prev) => (prev.includes(v) ? prev : [...prev, v]));
    setLocationInput('');
  };

  const isLocationInputBlocked = () =>
    emitInventoryMetadataLimitIfReached(
      LIMIT_ERROR_CODE.INVENTORY_LOCATION_LIMIT_REACHED,
      locations.length,
      maxInventoryLocations,
    );

  const isCategoryInputBlocked = () =>
    emitInventoryMetadataLimitIfReached(
      LIMIT_ERROR_CODE.INVENTORY_CATEGORY_LIMIT_REACHED,
      categories.length,
      maxInventoryCategories,
    );

  const handleToggleExpiryLeadDay = (day: number) => {
    setNotifyExpiryLeadDays((prev) =>
      prev.includes(day) ? prev.filter((d) => d !== day) : [...prev, day].sort((a, b) => a - b),
    );
  };

  const handleWizardNext = () => {
    setFormError(null);
    if (wizardStep === 1) {
      if (title.trim() === '') {
        setFormError(t('task.inventory.wizard.titleRequired'));
        return;
      }
      setWizardStep(2);
      return;
    }
    if (wizardStep === 2) {
      if (locations.length === 0) {
        setFormError(t('task.inventory.wizard.locationsRequired'));
        return;
      }
      setWizardStep(3);
      return;
    }
    if (wizardStep === 3) {
      if (categories.length === 0) {
        setFormError(t('task.inventory.wizard.categoriesRequired'));
        return;
      }
      setWizardStep(4);
      return;
    }
    if (wizardStep === 4) {
      if (recurrence === RECURRENCE_TYPE.WEEKLY || recurrence === RECURRENCE_TYPE.BIWEEKLY) {
        if (recurrenceDays.length === 0) {
          setFormError(t('task.form.recurrenceWeekdaysRequired'));
          return;
        }
      }
      if (recurrence === RECURRENCE_TYPE.MONTHLY) {
        if (monthlyKind === MONTHLY_KIND.DAY && recurrenceMonthDays.length === 0) {
          setFormError(t('task.form.recurrenceMonthDaysRequired'));
          return;
        }
        if (
          (monthlyKind === MONTHLY_KIND.NTH_WEEKDAY || monthlyKind === MONTHLY_KIND.LAST_WEEKDAY) &&
          recurrenceDays.length === 0
        ) {
          setFormError(t('task.form.recurrenceWeekdaysRequired'));
          return;
        }
      }
      if (recurrence === RECURRENCE_TYPE.NONE) {
        if (!/^\d{4}-\d{2}-\d{2}$/.test(occurrenceDate)) {
          setFormError(t('task.inventory.dueDateInvalid'));
          return;
        }
      } else if (!/^\d{4}-\d{2}-\d{2}$/.test(startDate)) {
        setFormError(t('task.inventory.dueDateInvalid'));
        return;
      }
      setWizardStep(5);
      return;
    }
    if (wizardStep === 5) {
      setWizardStep(6);
      return;
    }
    if (wizardStep === 6) {
      return;
    }
  };

  const handleKeyDown = (cb: () => void) => (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      cb();
    }
  };
  const renderClearDateButton = (onClear: () => void, ariaLabel: string) => (
    <button
      type="button"
      onClick={onClear}
      className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full text-gray-500 hover:bg-gray-100 hover:text-gray-700 focus:outline-none focus:ring-2 focus:ring-primary-500 dark:text-gray-300 dark:hover:bg-gray-700 dark:hover:text-gray-100"
      aria-label={ariaLabel}
    >
      <svg className="h-4 w-4" viewBox="0 0 20 20" fill="none" stroke="currentColor" aria-hidden>
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 6l8 8M14 6l-8 8" />
      </svg>
    </button>
  );

  const handleRecurrenceChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const value = e.target.value as RecurrenceType;
    setRecurrence(value);
    if (value === RECURRENCE_TYPE.MONTHLY) {
      const patch = emptyMonthlySchedulePatch(MONTHLY_KIND.DAY);
      setMonthlyKind(patch.monthlyKind);
      setRecurrenceDays(patch.recurrenceDays);
      setRecurrenceMonthDays(patch.recurrenceMonthDays);
      setRecurrenceOrdinal(patch.recurrenceOrdinal);
    } else {
      setMonthlyKind(null);
      setRecurrenceMonthDays([]);
      setRecurrenceOrdinal(null);
      setRecurrenceDays((prev) =>
        value === RECURRENCE_TYPE.NONE || value === RECURRENCE_TYPE.DAILY ? [] : prev,
      );
    }
    if (value === RECURRENCE_TYPE.NONE) {
      setExclusionRanges([]);
    }
    if (value !== RECURRENCE_TYPE.NONE) {
      setStartDate((prev) => prev || todayYmd);
    }
  };

  const handleDayToggle = (day: number) => {
    setRecurrenceDays((prev) => {
      const next = prev.includes(day) ? prev.filter((d) => d !== day) : [...prev, day].sort((a, b) => a - b);
      return next;
    });
  };

  const handleExclChange = (index: number, field: 'startDate' | 'endDate', value: string) => {
    setExclusionRanges((prev) => prev.map((r, i) => (i === index ? { ...r, [field]: value } : r)));
  };

  const handleAddExcl = () => {
    setExclusionRanges((prev) => [...prev, { startDate: '', endDate: '' }]);
  };

  const handleRemoveExcl = (index: number) => {
    setExclusionRanges((prev) => prev.filter((_, i) => i !== index));
  };

  const handleCreateList = async () => {
    const name = newListName.trim();
    if (name === '') {
      setFormError(t('task.inventory.listNameRequired'));
      return;
    }
    setCreatingList(true);
    setFormError(null);
    try {
      const res = await inventoryApi.createList({ name });
      if (!res.success || !res.data?.id) {
        setFormError(
          resolvePageErrorMessage(t('task.inventory.createListError'), {
            apiErrorMessage: res.error,
          }),
        );
        return;
      }
      setNewListName('');
      setCreateListOpen(false);
      await loadData();
      setSelectedListId(res.data.id);
      setEditListModalOpen(true);
      showSuccessToast(t('task.inventory.createListSuccess'));
    } catch (caught) {
      setFormError(resolvePageErrorMessage(t('task.inventory.createListError'), { caught }));
    } finally {
      setCreatingList(false);
    }
  };

  const handleOpenRenameListModal = () => {
    setEditingListName(selectedList?.name ?? '');
    setRenameListModalOpen(true);
  };

  const handleRenameSelectedList = async () => {
    if (!selectedListId) return;
    const name = editingListName.trim();
    if (name === '') {
      setFormError(t('task.inventory.listNameRequired'));
      return;
    }
    if (name === selectedList?.name) {
      return;
    }
    setFormError(null);
    try {
      const response = await inventoryApi.updateList(selectedListId, { name });
      if (!response.success) {
        setFormError(
          resolvePageErrorMessage(t('task.inventory.updateListError'), {
            apiErrorMessage: response.error,
          }),
        );
        return;
      }
      await loadData();
      showSuccessToast(t('task.inventory.updateListSuccess'));
      setRenameListModalOpen(false);
    } catch (caught) {
      setFormError(resolvePageErrorMessage(t('task.inventory.updateListError'), { caught }));
    }
  };

  const handleAddInventoryItem = async () => {
    const name = newItemName.trim();
    if (name === '') {
      setFormError(t('task.inventory.itemNameRequired'));
      return;
    }
    setAddingItem(true);
    setFormError(null);
    try {
      const res = await inventoryApi.createItem({ name, category: '' });
      if (!res.success || !res.data?.id) {
        setFormError(
          resolvePageErrorMessage(t('task.inventory.createItemError'), {
            apiErrorMessage: res.error,
          }),
        );
        return;
      }
      const newId = res.data.id;
      setNewItemName('');
      const ir = await inventoryApi.listItems();
      if (ir.success && ir.data) {
        setItems(ir.data.map((x) => ({ id: x.id, name: x.name })));
        setListItemSelection((p) => ({ ...p, [newId]: true }));
      }
      showSuccessToast(t('task.inventory.createItemSuccess'));
    } catch (caught) {
      setFormError(resolvePageErrorMessage(t('task.inventory.createItemError'), { caught }));
    } finally {
      setAddingItem(false);
    }
  };

  const selectedIdsForList = useMemo(
    () => visibleChecklistItems.filter((it) => listItemSelection[it.id]).map((it) => it.id),
    [visibleChecklistItems, listItemSelection],
  );

  const handleToggleItem = (itemId: string) => {
    setListItemSelection((p) => ({ ...p, [itemId]: !p[itemId] }));
  };

  const handleAfterInventoryUpdate = useCallback(async () => {
    const ir = await inventoryApi.listItems();
    if (ir.success && ir.data) {
      setItems(ir.data.map((x) => ({ id: x.id, name: x.name })));
    }
    if (selectedListId.trim() !== '') {
      await fetchListLines(selectedListId);
    }
  }, [fetchListLines, selectedListId]);

  const handleRemoveFromList = useCallback(
    async (itemId: string): Promise<boolean> => {
      if (!selectedListId) {
        return false;
      }
      setRemovingItemId(itemId);
      setFormError(null);
      try {
        const res = await inventoryApi.hideListItem(selectedListId, itemId);
        if (!res.success) {
          setFormError(
            resolvePageErrorMessage(t('task.inventory.saveListItemsError'), {
              apiErrorMessage: res.error,
            }),
          );
          return false;
        }
        await loadData();
        await fetchListLines(selectedListId);
        return true;
      } catch (caught) {
        setFormError(resolvePageErrorMessage(t('task.inventory.saveListItemsError'), { caught }));
        return false;
      } finally {
        setRemovingItemId(null);
      }
    },
    [fetchListLines, loadData, selectedListId, t],
  );

  const handleSaveListItems = async () => {
    if (!selectedListId) return;
    if (selectedIdsForList.length === 0) {
      setFormError(t('task.inventory.pickAtLeastOneItem'));
      return;
    }
    setSavingListItems(true);
    setFormError(null);
    try {
      const res = await inventoryApi.putListItems(selectedListId, selectedIdsForList);
      if (!res.success) {
        setFormError(
          resolvePageErrorMessage(t('task.inventory.saveListItemsError'), {
            apiErrorMessage: res.error,
          }),
        );
        return;
      }
      await loadData();
      await fetchListLines(selectedListId);
      showSuccessToast(t('task.inventory.saveListItemsSuccess'));
      setEditListModalOpen(false);
    } catch (caught) {
      setFormError(resolvePageErrorMessage(t('task.inventory.saveListItemsError'), { caught }));
    } finally {
      setSavingListItems(false);
    }
  };

  const handleConfirmRemoveList = async () => {
    if (!selectedListId) return;
    setFormError(null);
    setRemoveListLoading(true);
    try {
      const response = await inventoryApi.removeList(selectedListId);
      if (!response.success) {
        setFormError(
          resolvePageErrorMessage(t('task.inventory.removeListError'), {
            apiErrorMessage: response.error,
          }),
        );
        return;
      }
      setRemoveListConfirmOpen(false);
      setEditListModalOpen(false);
      setAffectedActiveTasks([]);
      await loadData();
      showSuccessToast(t('task.inventory.removeListSuccess'));
    } catch (caught) {
      setFormError(resolvePageErrorMessage(t('task.inventory.removeListError'), { caught }));
    } finally {
      setRemoveListLoading(false);
    }
  };

  const handleCreateTask = async () => {
    if (title.trim() === '') {
      setFormError(t('task.inventory.wizard.titleRequired'));
      return;
    }
    if (categories.length === 0) {
      setFormError(t('task.inventory.wizard.categoriesRequired'));
      return;
    }
    if (locations.length === 0) {
      setFormError(t('task.inventory.wizard.locationsRequired'));
      return;
    }
    if (recurrence === RECURRENCE_TYPE.WEEKLY || recurrence === RECURRENCE_TYPE.BIWEEKLY) {
      if (recurrenceDays.length === 0) {
        setFormError(t('task.form.recurrenceWeekdaysRequired'));
        return;
      }
    }
    if (recurrence === RECURRENCE_TYPE.MONTHLY) {
      if (monthlyKind === MONTHLY_KIND.DAY && recurrenceMonthDays.length === 0) {
        setFormError(t('task.form.recurrenceMonthDaysRequired'));
        return;
      }
      if (
        (monthlyKind === MONTHLY_KIND.NTH_WEEKDAY || monthlyKind === MONTHLY_KIND.LAST_WEEKDAY) &&
        recurrenceDays.length === 0
      ) {
        setFormError(t('task.form.recurrenceWeekdaysRequired'));
        return;
      }
    }
    if (recurrence === RECURRENCE_TYPE.NONE) {
      if (!/^\d{4}-\d{2}-\d{2}$/.test(occurrenceDate)) {
        setFormError(t('task.inventory.dueDateInvalid'));
        return;
      }
    } else if (!/^\d{4}-\d{2}-\d{2}$/.test(startDate)) {
      setFormError(t('task.inventory.dueDateInvalid'));
      return;
    }
    setSubmitting(true);
    setFormError(null);
    try {
      const res = await inventoryApi.createTask({
        title: title.trim(),
        description: description.trim(),
        recurrence,
        recurrenceDays,
        monthlyKind,
        recurrenceMonthDays,
        recurrenceOrdinal,
        occurrenceDate: recurrence === RECURRENCE_TYPE.NONE ? occurrenceDate : '',
        startDate: recurrence === RECURRENCE_TYPE.NONE ? '' : startDate,
        endDate: recurrence === RECURRENCE_TYPE.NONE ? '' : endDate,
        exclusionRanges:
          recurrence === RECURRENCE_TYPE.NONE
            ? []
            : exclusionRanges.filter((r) => r.startDate && r.endDate),
        dueTime,
        requirePhoto: true,
        notifyEmployer,
        remindHelperBeforeDue: dueTime.trim() !== '' && remindHelperBeforeDue,
        categories,
        locations,
        notifyExpiryLeadDays,
      });
      if (!res.success || !res.data?.taskId) {
        setFormError(
          resolvePageErrorMessage(t('task.inventory.createTaskError'), {
            apiErrorMessage: res.error,
          }),
        );
        return;
      }
      showSuccessToast(t('task.inventory.createTaskSuccess'));
      navigate('/tasks');
    } catch (caught) {
      if (isLimitReachedError(caught)) {
        return;
      }
      setFormError(resolvePageErrorMessage(t('task.inventory.createTaskError'), { caught }));
    } finally {
      setSubmitting(false);
    }
  };

  const showDaysSelector =
    recurrence === RECURRENCE_TYPE.WEEKLY || recurrence === RECURRENCE_TYPE.BIWEEKLY;
  const isMonthly = recurrence === RECURRENCE_TYPE.MONTHLY;
  const isOneTimeTask = recurrence === RECURRENCE_TYPE.NONE;

  const recurrenceSummaryText = useMemo(() => {
    if (recurrence === RECURRENCE_TYPE.NONE) {
      return `${t('task.recurrence.none')} · ${occurrenceDate}`;
    }
    if (recurrence === RECURRENCE_TYPE.MONTHLY) {
      const tail = endDate !== '' ? ` · ${t('task.form.endDateLabel')}: ${endDate}` : '';
      return `${t('task.recurrence.monthly')} · ${t('task.form.startDateLabel')}: ${startDate}${tail}`;
    }
    const head =
      recurrence === RECURRENCE_TYPE.DAILY
        ? t('task.recurrence.daily')
        : recurrence === RECURRENCE_TYPE.WEEKLY
          ? t('task.recurrence.weekly')
          : t('task.recurrence.biweekly');
    const tail = endDate !== '' ? ` · ${t('task.form.endDateLabel')}: ${endDate}` : '';
    return `${head} · ${t('task.form.startDateLabel')}: ${startDate}${tail}`;
  }, [recurrence, occurrenceDate, startDate, endDate, t]);

  const inventoryReviewRows = useMemo(
    () =>
      buildInventoryWizardReviewRows({
        t,
        title,
        description,
        categories,
        locations,
        recurrenceSummaryText,
        dueTime,
        notifyEmployer,
        notifyExpiryLeadDays,
      }),
    [
      t,
      title,
      description,
      categories,
      locations,
      recurrenceSummaryText,
      dueTime,
      notifyEmployer,
      notifyExpiryLeadDays,
    ],
  );

  if (loading) {
    return (
      <div className="flex min-h-full items-center justify-center bg-gray-50 dark:bg-gray-950">
        <div
          className="h-10 w-10 animate-spin rounded-full border-4 border-primary-200 border-t-primary-600 dark:border-primary-800 dark:border-t-primary-400"
          aria-hidden
        />
      </div>
    );
  }

  return (
    <div className="min-h-full bg-gray-50 dark:bg-gray-950">
      <header className="z-10 border-b border-gray-200 bg-white shadow-sm dark:border-gray-800 dark:bg-gray-900">
        <div className="mx-auto flex max-w-lg items-center gap-3 px-4 py-4">
          <button
            type="button"
            onClick={handleBack}
            className="flex h-10 w-10 items-center justify-center rounded-full text-gray-600 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-primary-500 dark:text-gray-300 dark:hover:bg-gray-800"
            aria-label={t('task.action.back')}
          >
            <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </button>
          <h1 className="text-lg font-bold text-gray-900 dark:text-gray-100">{t('task.inventory.newTaskTitle')}</h1>
        </div>
      </header>

      <main className="mx-auto max-w-lg space-y-6 px-4 py-6 pb-24">
        {loadError ? (
          <p className="text-center text-red-600 dark:text-red-400" role="alert">
            {loadError}
          </p>
        ) : null}

        <div
          className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm dark:border-gray-700 dark:bg-gray-900"
          role="status"
          aria-live="polite"
        >
          <p className="text-xs font-medium text-gray-500 dark:text-gray-400">
            {t('task.inventory.wizard.stepOf', { current: wizardStep, total: WIZARD_TOTAL_STEPS })}
          </p>
          <p className="mt-1 text-base font-semibold text-gray-900 dark:text-gray-100">
            {t(WIZARD_STEP_TITLE_KEYS[wizardStep - 1] ?? WIZARD_STEP_TITLE_KEYS[0])}
          </p>
          <div className="mt-3 h-2 w-full overflow-hidden rounded-full bg-gray-200 dark:bg-gray-700">
            <div
              className="h-2 rounded-full bg-primary-600 transition-all dark:bg-primary-500"
              style={{ width: `${(wizardStep / WIZARD_TOTAL_STEPS) * 100}%` }}
            />
          </div>
        </div>

        {wizardStep === -1 ? (
          <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm dark:border-gray-700 dark:bg-gray-900">
            <div className="flex items-center justify-between gap-2">
              <h2 className="text-base font-semibold text-gray-900 dark:text-gray-100">
                {t('task.inventory.sections.lists')}
              </h2>
              <button
                type="button"
                onClick={() => {
                  setCreateListOpen((v) => !v);
                  setFormError(null);
                }}
                className="min-h-10 rounded-lg border border-primary-200 px-3 text-sm font-medium text-primary-700 hover:bg-primary-50 focus:outline-none focus:ring-2 focus:ring-primary-400 dark:border-primary-800 dark:text-primary-300 dark:hover:bg-primary-950/50"
              >
                {t('task.inventory.createListCta')}
              </button>
            </div>
            {lists.length === 0 ? (
              <p className="mt-2 text-sm text-amber-800 dark:text-amber-200">{t('task.inventory.noListsHint')}</p>
            ) : null}
          </div>
        ) : null}

        {createListOpen ? (
          <div
            className={`${MODAL_BACKDROP_CENTER_CLASS} z-50 ${MODAL_BACKDROP_SCRIM_CLASS}`}
            role="dialog"
            aria-modal="true"
            aria-labelledby="inv-new-list-title"
          >
            <div
              className={`w-full max-w-md rounded-xl bg-white p-5 shadow-xl dark:bg-gray-900 dark:ring-1 dark:ring-gray-700 ${MODAL_PANEL_SCROLL_DVH_CLASS}`}
            >
              <h2 id="inv-new-list-title" className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                {t('task.inventory.createListTitle')}
              </h2>
              <label className="mt-3 block text-sm font-medium text-gray-700 dark:text-gray-200" htmlFor="inv-list-name">
                {t('task.inventory.listNameLabel')}
              </label>
              <input
                id="inv-list-name"
                value={newListName}
                onChange={(e) => setNewListName(e.target.value)}
                className="mt-1 w-full min-h-11 rounded-lg border border-gray-300 px-3 py-2 text-gray-900 focus:outline-none focus:ring-2 focus:ring-primary-400 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100"
                maxLength={200}
                autoComplete="off"
              />
              <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">{t('task.inventory.listScheduleMovedHint')}</p>
              <div className="mt-4 flex justify-end gap-2">
                <AppButton type="button" variant="ghost" onClick={() => setCreateListOpen(false)}>
                  {t('common.cancel')}
                </AppButton>
                <AppButton
                  type="button"
                  variant="primary"
                  onClick={() => void handleCreateList()}
                  disabled={creatingList}
                >
                  {creatingList ? t('common.loading') : t('common.confirm')}
                </AppButton>
              </div>
            </div>
          </div>
        ) : null}

        {selectedList && selectedListId && editListModalOpen ? (
          <div
            className={`${MODAL_BACKDROP_CENTER_CLASS} z-50 ${MODAL_BACKDROP_SCRIM_CLASS}`}
            role="dialog"
            aria-modal="true"
            aria-labelledby="inv-new-edit-list-items-title"
          >
          <div className={`w-full max-w-md rounded-xl border border-gray-200 bg-white p-4 shadow-sm dark:border-gray-700 dark:bg-gray-900 ${MODAL_PANEL_SCROLL_DVH_CLASS}`}>
            {selectedList.itemCount === 0 ? (
              <div className="mb-4 rounded-lg border border-amber-200 bg-amber-50 p-3 dark:border-amber-900/50 dark:bg-amber-950/30">
                <h3 className="text-sm font-semibold text-amber-900 dark:text-amber-100">
                  {t('task.inventory.assignItemsTitle')}
                </h3>
                <p className="mt-1 text-xs text-amber-900/90 dark:text-amber-100/90">{t('task.inventory.assignItemsHint')}</p>
              </div>
            ) : null}
            <h3 id="inv-new-edit-list-items-title" className="text-sm font-semibold text-gray-900 dark:text-gray-100">
              {t('task.inventory.editChecklistItemsHeading')}
            </h3>
            <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">{t('task.inventory.createListUseHint')}</p>
            <div className="mt-3 flex items-center justify-between rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 dark:border-gray-700 dark:bg-gray-800/60">
              <p className="truncate text-sm font-medium text-gray-800 dark:text-gray-100">{selectedList.name}</p>
              <button
                type="button"
                onClick={handleOpenRenameListModal}
                className="inline-flex h-10 w-10 items-center justify-center rounded-lg border border-gray-300 bg-white text-gray-800 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-primary-400 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100"
                aria-label={t('task.inventory.renameListModalOpenCta')}
              >
                <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                </svg>
              </button>
            </div>
            <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">{t('task.inventory.newTaskChecklistHint')}</p>
            {listLinesLoading && items.length > 0 ? (
              <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">{t('common.loading')}</p>
            ) : null}
            {!listLinesLoading && visibleChecklistItems.length > 0 ? (
              <>
                <ul className="mt-3 max-h-56 space-y-2 overflow-y-auto rounded-lg border border-gray-100 p-2 dark:border-gray-700" role="list">
                  {visibleChecklistItems.map((it) => (
                    <InventoryChecklistItemRow
                      key={it.id}
                      item={it}
                      idPrefix="inv-new"
                      checked={Boolean(listItemSelection[it.id])}
                      listActionsEnabled
                      isRowBusy={removingItemId === it.id}
                      onToggle={() => handleToggleItem(it.id)}
                      onRemoveFromList={() => handleRemoveFromList(it.id)}
                      onInventoryChanged={handleAfterInventoryUpdate}
                    />
                  ))}
                </ul>
                <p className="mt-2 text-xs text-gray-600 dark:text-gray-400">
                  {t('task.inventory.maxItemsNote', {
                    count: selectedIdsForList.length,
                  })}
                </p>
              </>
            ) : null}
            {!listLinesLoading && items.length === 0 ? (
              <p className="mt-2 text-sm text-amber-800 dark:text-amber-200" role="status">
                {t('task.inventory.assignItemsHint')}
              </p>
            ) : null}
            <div className="mt-3 flex flex-wrap items-center gap-2">
              <input
                value={newItemName}
                onChange={(e) => setNewItemName(e.target.value)}
                placeholder={t('task.inventory.quickAddItem')}
                className="min-h-11 min-w-[10rem] flex-1 rounded-lg border border-gray-300 px-3 text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-primary-400 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100"
                maxLength={200}
                aria-label={t('task.inventory.quickAddItem')}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    void handleAddInventoryItem();
                  }
                }}
              />
              <button
                type="button"
                onClick={() => void handleAddInventoryItem()}
                disabled={addingItem}
                className="min-h-11 shrink-0 rounded-lg border border-gray-300 bg-white px-3 text-sm font-medium text-gray-800 hover:bg-gray-50 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100"
              >
                {addingItem ? t('common.loading') : t('task.inventory.addItemCta')}
              </button>
            </div>
            <div className="mt-4">
              <AppButton
                type="button"
                variant="primary"
                fullWidth
                onClick={() => void handleSaveListItems()}
                disabled={savingListItems || !selectedListId || listLinesLoading}
              >
                {savingListItems ? t('common.loading') : t('task.inventory.saveListItemsCta')}
              </AppButton>
            </div>
            <AppButton
              type="button"
              variant="secondary"
              fullWidth
              className="mt-3"
              onClick={() => setEditListModalOpen(false)}
            >
              {t('common.cancel')}
            </AppButton>
          </div>
          </div>
        ) : null}

        {selectedList && renameListModalOpen ? (
          <div
            className={`${MODAL_BACKDROP_CENTER_CLASS} z-[60] ${MODAL_BACKDROP_SCRIM_CLASS}`}
            role="dialog"
            aria-modal="true"
            aria-labelledby="inv-new-rename-list-title"
          >
            <div className="w-full max-w-sm rounded-xl border border-gray-200 bg-white p-4 shadow-sm dark:border-gray-700 dark:bg-gray-900">
              <h3 id="inv-new-rename-list-title" className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                {t('task.inventory.renameListModalTitle')}
              </h3>
              <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">{selectedList.name}</p>
              <input
                value={editingListName}
                onChange={(e) => setEditingListName(e.target.value)}
                className="mt-3 min-h-11 w-full rounded-lg border border-gray-300 px-3 text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-primary-400 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100"
                maxLength={200}
                aria-label={t('task.inventory.listNameLabel')}
              />
              <div className="mt-3 flex items-center justify-end gap-2">
                <AppButton type="button" variant="secondary" onClick={() => setRenameListModalOpen(false)}>
                  {t('common.cancel')}
                </AppButton>
                <AppButton type="button" variant="primary" onClick={() => void handleRenameSelectedList()}>
                  {t('task.inventory.renameItemSave')}
                </AppButton>
              </div>
            </div>
          </div>
        ) : null}

        <form
          onSubmit={(e) => {
            e.preventDefault();
          }}
          className={
            wizardStep === 6
              ? 'space-y-4'
              : 'space-y-4 rounded-xl border border-gray-200 bg-white p-4 shadow-sm dark:border-gray-700 dark:bg-gray-900'
          }
        >
          {wizardStep !== 6 ? (
            <h2 className="text-base font-semibold text-gray-900 dark:text-gray-100">
              {t('task.inventory.formHeading')}
            </h2>
          ) : null}
          {wizardStep === 1 ? (
            <>
              <div>
                <label className="text-sm font-medium text-gray-700 dark:text-gray-200" htmlFor="inv-title">
                  {t('task.form.titleLabel')}
                </label>
                <input
                  id="inv-title"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  className="mt-1 w-full min-h-11 rounded-lg border border-gray-300 px-3 text-gray-900 focus:outline-none focus:ring-2 focus:ring-primary-400 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100"
                  maxLength={120}
                  placeholder={t('task.inventory.titlePlaceholder')}
                  autoComplete="off"
                />
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700 dark:text-gray-200" htmlFor="inv-desc">
                  {t('task.form.descLabelOptional')}
                </label>
                <textarea
                  id="inv-desc"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  rows={3}
                  maxLength={1000}
                  className="mt-1 w-full resize-none rounded-lg border border-gray-300 px-3 py-2 text-gray-900 focus:outline-none focus:ring-2 focus:ring-primary-400 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100"
                />
              </div>
            </>
          ) : null}
          {wizardStep === 2 ? (
            <div>
              <p className="text-sm text-gray-600 dark:text-gray-300">{t('task.inventory.wizard.locationsHint')}</p>
              <div className="mt-2 flex min-w-0 gap-2">
                <input
                  value={locationInput}
                  onChange={(e) => setLocationInput(e.target.value)}
                  onFocus={(event) => {
                    if (isLocationInputBlocked()) {
                      event.currentTarget.blur();
                    }
                  }}
                  onClick={(event) => {
                    if (isLocationInputBlocked()) {
                      event.currentTarget.blur();
                    }
                  }}
                  placeholder={t('task.inventory.wizard.addLocationPlaceholder')}
                  className="min-w-0 flex-1 min-h-11 rounded-lg border border-gray-300 px-3 text-gray-900 focus:outline-none focus:ring-2 focus:ring-primary-400 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100"
                  maxLength={150}
                  aria-label={t('task.inventory.wizard.addLocationPlaceholder')}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      handleAddLocationChip(locationInput);
                    }
                  }}
                />
                <AppButton
                  type="button"
                  variant="primary"
                  className="shrink-0"
                  onClick={() => handleAddLocationChip(locationInput)}
                >
                  {t('task.inventory.wizard.addChip')}
                </AppButton>
              </div>
              {locations.length > 0 ? (
                <ul className="mt-3 flex flex-wrap gap-2" role="list">
                  {locations.map((loc) => (
                    <li key={loc}>
                      <span className="inline-flex items-center gap-1 rounded-full bg-emerald-100 px-3 py-1 text-sm text-emerald-900 dark:bg-emerald-900/40 dark:text-emerald-100">
                        {loc}
                        <button
                          type="button"
                          onClick={() => setLocations((prev) => prev.filter((x) => x !== loc))}
                          onKeyDown={handleKeyDown(() => setLocations((prev) => prev.filter((x) => x !== loc)))}
                          tabIndex={0}
                          className="rounded-full p-0.5 hover:bg-emerald-200 focus:outline-none focus:ring-2 focus:ring-emerald-500 dark:hover:bg-emerald-800"
                          aria-label={t('task.inventory.wizard.removeChipAria', { label: loc })}
                        >
                          ×
                        </button>
                      </span>
                    </li>
                  ))}
                </ul>
              ) : null}
              {suggestLocations.length > 0 ? (
                <div className="mt-3">
                  <p className="text-xs font-medium text-gray-500 dark:text-gray-400">
                    {t('task.inventory.wizard.suggestHeading')}
                  </p>
                  <div className="mt-1 flex flex-wrap gap-2">
                    {suggestLocations.map((s) => (
                      <button
                        key={s}
                        type="button"
                        onClick={() => handleAddLocationChip(s)}
                        onKeyDown={handleKeyDown(() => handleAddLocationChip(s))}
                        tabIndex={0}
                        className="rounded-lg border border-gray-200 px-2 py-1 text-xs text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-primary-400 dark:border-gray-600 dark:text-gray-200 dark:hover:bg-gray-800"
                      >
                        {locationSuggestionLabelMap.get(s) ?? s}
                      </button>
                    ))}
                  </div>
                </div>
              ) : null}
            </div>
          ) : null}
          {wizardStep === 3 ? (
            <div>
              <p className="text-sm text-gray-600 dark:text-gray-300">{t('task.inventory.wizard.categoriesHint')}</p>
              <div className="mt-2 flex min-w-0 gap-2">
                <input
                  value={categoryInput}
                  onChange={(e) => setCategoryInput(e.target.value)}
                  onFocus={(event) => {
                    if (isCategoryInputBlocked()) {
                      event.currentTarget.blur();
                    }
                  }}
                  onClick={(event) => {
                    if (isCategoryInputBlocked()) {
                      event.currentTarget.blur();
                    }
                  }}
                  placeholder={t('task.inventory.wizard.addCategoryPlaceholder')}
                  className="min-w-0 flex-1 min-h-11 rounded-lg border border-gray-300 px-3 text-gray-900 focus:outline-none focus:ring-2 focus:ring-primary-400 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100"
                  maxLength={120}
                  aria-label={t('task.inventory.wizard.addCategoryPlaceholder')}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      handleAddCategoryChip(categoryInput);
                    }
                  }}
                />
                <AppButton
                  type="button"
                  variant="primary"
                  className="shrink-0"
                  onClick={() => handleAddCategoryChip(categoryInput)}
                >
                  {t('task.inventory.wizard.addChip')}
                </AppButton>
              </div>
              {categories.length > 0 ? (
                <ul className="mt-3 flex flex-wrap gap-2" role="list">
                  {categories.map((c) => (
                    <li key={c}>
                      <span className="inline-flex items-center gap-1 rounded-full bg-primary-100 px-3 py-1 text-sm text-primary-900 dark:bg-primary-900/40 dark:text-primary-100">
                        {c}
                        <button
                          type="button"
                          onClick={() => setCategories((prev) => prev.filter((x) => x !== c))}
                          onKeyDown={handleKeyDown(() => setCategories((prev) => prev.filter((x) => x !== c)))}
                          tabIndex={0}
                          className="rounded-full p-0.5 hover:bg-primary-200 focus:outline-none focus:ring-2 focus:ring-primary-500 dark:hover:bg-primary-800"
                          aria-label={t('task.inventory.wizard.removeChipAria', { label: c })}
                        >
                          ×
                        </button>
                      </span>
                    </li>
                  ))}
                </ul>
              ) : null}
              {suggestCategories.length > 0 ? (
                <div className="mt-3">
                  <p className="text-xs font-medium text-gray-500 dark:text-gray-400">
                    {t('task.inventory.wizard.suggestHeading')}
                  </p>
                  <div className="mt-1 flex flex-wrap gap-2">
                    {suggestCategories.map((s) => (
                      <button
                        key={s}
                        type="button"
                        onClick={() => handleAddCategoryChip(s)}
                        onKeyDown={handleKeyDown(() => handleAddCategoryChip(s))}
                        tabIndex={0}
                        className="rounded-lg border border-gray-200 px-2 py-1 text-xs text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-primary-400 dark:border-gray-600 dark:text-gray-200 dark:hover:bg-gray-800"
                      >
                        {categorySuggestionLabelMap.get(s) ?? s}
                      </button>
                    ))}
                  </div>
                </div>
              ) : null}
            </div>
          ) : null}
          {wizardStep === 4 ? (
          <>
          <div>
            <label className="text-sm font-medium text-gray-700 dark:text-gray-200" htmlFor="inv-recurrence">
              {t('task.form.recurrenceLabel')}
            </label>
            <select
              id="inv-recurrence"
              value={recurrence}
              onChange={handleRecurrenceChange}
              className="mt-1 w-full min-h-11 rounded-lg border border-gray-300 bg-white px-3 text-gray-900 focus:outline-none focus:ring-2 focus:ring-primary-400 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100"
            >
              <option value={RECURRENCE_TYPE.NONE}>{t('task.recurrence.none')}</option>
              <option value={RECURRENCE_TYPE.DAILY}>{t('task.recurrence.daily')}</option>
              <option value={RECURRENCE_TYPE.WEEKLY}>{t('task.recurrence.weekly')}</option>
              <option value={RECURRENCE_TYPE.BIWEEKLY}>{t('task.recurrence.biweekly')}</option>
              <option value={RECURRENCE_TYPE.MONTHLY}>{t('task.recurrence.monthly')}</option>
            </select>
          </div>
          {isMonthly ? (
            <TaskMonthlyScheduleControls
              t={t}
              idPrefix="inv-monthly"
              monthlyKind={monthlyKind}
              recurrenceDays={recurrenceDays}
              recurrenceMonthDays={recurrenceMonthDays}
              recurrenceOrdinal={recurrenceOrdinal}
              onChange={(patch) => {
                setMonthlyKind(patch.monthlyKind);
                setRecurrenceDays(patch.recurrenceDays);
                setRecurrenceMonthDays(patch.recurrenceMonthDays);
                setRecurrenceOrdinal(patch.recurrenceOrdinal);
              }}
            />
          ) : null}
          {showDaysSelector ? (
            <div>
              <span className="text-sm font-medium text-gray-700 dark:text-gray-200">{t('task.form.daysLabel')}</span>
              <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">{t('task.form.daysHint')}</p>
              <div className="mt-2 flex flex-wrap gap-2" role="group" aria-label={t('task.form.daysLabel')}>
                {REC_DAYS.map((day) => (
                  <button
                    key={day}
                    type="button"
                    onClick={() => handleDayToggle(day)}
                    onKeyDown={handleKeyDown(() => handleDayToggle(day))}
                    tabIndex={0}
                    className={
                      recurrenceDays.includes(day)
                        ? 'flex h-10 w-10 items-center justify-center rounded-full text-sm font-medium bg-primary-600 text-white focus:outline-none focus:ring-2 focus:ring-primary-500'
                        : 'flex h-10 w-10 items-center justify-center rounded-full text-sm font-medium bg-gray-100 text-gray-700 hover:bg-gray-200 dark:bg-gray-800 dark:text-gray-200 dark:hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-primary-500'
                    }
                    aria-pressed={recurrenceDays.includes(day)}
                    aria-label={t(REC_DAY_KEYS[day])}
                  >
                    {t(REC_DAY_KEYS[day])}
                  </button>
                ))}
              </div>
            </div>
          ) : null}
          {isOneTimeTask ? (
            <div>
              <label
                className="text-sm font-medium text-gray-700 dark:text-gray-200"
                htmlFor="inv-occurrence"
              >
                {t('task.form.occurrenceDateLabel')}
              </label>
              <IsoDatePicker
                id="inv-occurrence"
                ariaLabel={t('task.form.occurrenceDateLabel')}
                value={occurrenceDate}
                minDate={todayYmd}
                onChange={setOccurrenceDate}
                valueFormat="iso"
                className="mt-1 w-full"
              />
            </div>
          ) : (
            <>
              <div>
                <label className="text-sm font-medium text-gray-700 dark:text-gray-200" htmlFor="inv-start">
                  {t('task.form.startDateLabel')}
                </label>
                <IsoDatePicker
                  id="inv-start"
                  ariaLabel={t('task.form.startDateLabel')}
                  value={startDate}
                  maxDate={endDate || undefined}
                  onChange={setStartDate}
                  valueFormat="iso"
                  className="mt-1 w-full"
                />
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700 dark:text-gray-200" htmlFor="inv-end">
                  {t('task.form.endDateLabelOptional')}
                </label>
                <div className="mt-1 flex min-w-0 items-center gap-2">
                  <IsoDatePicker
                    id="inv-end"
                    ariaLabel={t('task.form.endDateLabelOptional')}
                    value={endDate}
                    minDate={startDate || undefined}
                    onChange={setEndDate}
                    valueFormat="iso"
                    className="min-w-0 flex-1"
                  />
                  {endDate !== ''
                    ? renderClearDateButton(() => setEndDate(''), t('task.form.endDateLabel'))
                    : null}
                </div>
              </div>
              <div>
                <div className="flex items-center justify-between gap-2">
                  <span className="text-sm font-medium text-gray-700 dark:text-gray-200">
                    {t('task.form.excludeRangesLabel')}
                  </span>
                  <button
                    type="button"
                    onClick={handleAddExcl}
                    className="rounded-lg border border-primary-300 bg-white px-2 py-1 text-xs font-medium text-primary-700 hover:bg-primary-50 focus:outline-none focus:ring-2 focus:ring-primary-500 dark:border-primary-800 dark:bg-gray-900 dark:hover:bg-primary-950/50"
                  >
                    {t('task.form.addExcludeRange')}
                  </button>
                </div>
                <div className="mt-2 space-y-2">
                  {exclusionRanges.map((range, index) => (
                    <div
                      key={`excl-${index}`}
                      className="rounded-lg border border-gray-200 p-3 dark:border-gray-600"
                    >
                      <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                        <div className="flex min-w-0 items-center gap-2">
                          <IsoDatePicker
                            id={`inv-exclude-start-${index}`}
                            ariaLabel={t('task.form.excludeStartDateLabel')}
                            value={range.startDate}
                            maxDate={range.endDate || undefined}
                            onChange={(next) => handleExclChange(index, 'startDate', next)}
                            valueFormat="iso"
                            className="min-w-0 flex-1"
                          />
                          {range.startDate !== ''
                            ? renderClearDateButton(
                              () => handleExclChange(index, 'startDate', ''),
                              t('task.form.excludeStartDateLabel'),
                            )
                            : null}
                        </div>
                        <div className="flex min-w-0 items-center gap-2">
                          <IsoDatePicker
                            id={`inv-exclude-end-${index}`}
                            ariaLabel={t('task.form.excludeEndDateLabel')}
                            value={range.endDate}
                            minDate={range.startDate || undefined}
                            onChange={(next) => handleExclChange(index, 'endDate', next)}
                            valueFormat="iso"
                            className="min-w-0 flex-1"
                          />
                          {range.endDate !== ''
                            ? renderClearDateButton(
                              () => handleExclChange(index, 'endDate', ''),
                              t('task.form.excludeEndDateLabel'),
                            )
                            : null}
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={() => handleRemoveExcl(index)}
                        className="mt-2 rounded-lg border border-red-200 bg-white px-2 py-1 text-xs font-medium text-red-700 hover:bg-red-50 focus:outline-none focus:ring-2 focus:ring-red-400 dark:border-red-900 dark:bg-gray-900 dark:text-red-300"
                      >
                        {t('task.form.removeExcludeRange')}
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            </>
          )}
          </>
          ) : null}
          {wizardStep === 5 ? (
          <>
          <div>
            <label className="text-sm font-medium text-gray-700 dark:text-gray-200" htmlFor="inv-duetime">
              {t('task.form.dueTimeLabelOptional')}
            </label>
            <div className="mt-1 flex min-w-0 items-center gap-2">
              <NativeTimeInput
                id="inv-duetime"
                value={dueTime}
                onChange={(e) => setDueTime(e.target.value)}
                wrapperClassName="min-w-0 flex-1"
                className="min-h-11 w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-primary-400 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100"
              />
              {dueTime !== ''
                ? renderClearDateButton(() => setDueTime(''), t('task.form.dueTimeLabel'))
                : null}
            </div>
          </div>
          <div className="flex items-center gap-3">
            <input
              id="inv-notify"
              type="checkbox"
              checked={notifyEmployer}
              onChange={(e) => setNotifyEmployer(e.target.checked)}
              className="h-5 w-5 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
            />
            <label htmlFor="inv-notify" className="text-sm font-medium text-gray-700 dark:text-gray-200">
              {t('task.form.notifyLabel')}
            </label>
          </div>
          <HelperDueReminderCheckbox
            id="inv-remind-helper"
            checked={remindHelperBeforeDue}
            disabled={dueTime.trim() === ''}
            onChange={setRemindHelperBeforeDue}
            t={t}
          />
          <div>
            <span className="text-sm font-medium text-gray-700 dark:text-gray-200">
              {t('task.inventory.wizard.expiryLeadsLabel')}
            </span>
            <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">{t('task.inventory.wizard.expiryLeadsHint')}</p>
            <div className="mt-2 flex flex-wrap gap-2" role="group" aria-label={t('task.inventory.wizard.expiryLeadsLabel')}>
              {EXPIRY_LEAD_OPTIONS.map((day) => {
                const selected = notifyExpiryLeadDays.includes(day);
                return (
                <button
                  key={day}
                  type="button"
                  onClick={() => handleToggleExpiryLeadDay(day)}
                  onKeyDown={handleKeyDown(() => handleToggleExpiryLeadDay(day))}
                  tabIndex={0}
                  className={
                    selected
                      ? 'relative min-h-10 rounded-lg border-2 border-primary-600 bg-primary-50 px-3 py-2 text-sm font-medium text-primary-800 dark:bg-primary-950/50 dark:text-primary-100'
                      : 'relative min-h-10 rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-200 dark:hover:bg-gray-700'
                  }
                  aria-pressed={selected}
                >
                  {day}
                  {selected ? (
                    <span
                      className="absolute bottom-1 right-1 flex h-3.5 w-3.5 items-center justify-center text-primary-700 dark:text-primary-300"
                      aria-hidden
                    >
                      <svg className="h-3.5 w-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                      </svg>
                    </span>
                  ) : null}
                </button>
                );
              })}
            </div>
          </div>
          </>
          ) : null}
          {wizardStep === 6 ? (
            <InventoryWizardReviewStep
              cardHeading={t('task.inventory.wizard.reviewCardHeading')}
              rows={inventoryReviewRows}
              backLabel={t('task.inventory.wizard.back')}
              primaryLabel={t('task.inventory.wizard.reviewConfirm')}
              primaryBusyLabel={t('common.loading')}
              isPrimaryBusy={submitting}
              isBackDisabled={submitting}
              isPrimaryDisabled={submitting}
              onBack={handleWizardPrev}
              onPrimary={() => void handleCreateTask()}
            />
          ) : null}
          {formError ? (
            <p className="text-sm text-red-600 dark:text-red-400" role="alert">
              {formError}
            </p>
          ) : null}
          {wizardStep !== 6 ? (
            <div className="flex flex-col gap-2 sm:flex-row sm:justify-between">
              {wizardStep > 1 ? (
                <AppButton
                  type="button"
                  variant="secondary"
                  size="lg"
                  onClick={handleWizardPrev}
                  disabled={submitting}
                  className="w-full sm:w-auto sm:min-w-[8rem]"
                >
                  {t('task.inventory.wizard.back')}
                </AppButton>
              ) : (
                <span className="hidden sm:block sm:w-auto" aria-hidden />
              )}
              {wizardStep < WIZARD_TOTAL_STEPS ? (
                <AppButton
                  type="button"
                  variant="primary"
                  size="lg"
                  onClick={handleWizardNext}
                  className="w-full sm:ml-auto sm:w-auto sm:min-w-[8rem]"
                >
                  {t('task.inventory.wizard.next')}
                </AppButton>
              ) : null}
            </div>
          ) : null}
        </form>
      </main>
      <ConfirmDialog
        open={removeListConfirmOpen}
        title={t('task.inventory.removeListConfirmTitle')}
        description={t('task.inventory.removeListConfirmBody')}
        confirmLabel={removeListLoading ? t('common.loading') : t('task.inventory.removeListConfirmCta')}
        cancelLabel={t('common.cancel')}
        onConfirm={() => void handleConfirmRemoveList()}
        onCancel={() => {
          if (!removeListLoading) {
            setRemoveListConfirmOpen(false);
          }
        }}
        variant="danger"
        confirmDisabled={removeListLoading}
        cancelDisabled={removeListLoading}
      >
        <div className="mt-2">
          <p className="mb-1 text-xs font-semibold text-rose-200">
            {t('task.inventory.removeListAffectedTasksCount', { count: affectedActiveTasks.length })}
          </p>
          {affectedActiveTasks.length > 0 ? (
            <>
              <p className="mb-1 text-xs font-semibold text-rose-200">
              {t('task.inventory.removeListAffectedTasksLabel')}
            </p>
            <ul className="max-h-40 space-y-1 overflow-y-auto rounded-lg border border-rose-200 bg-rose-50 p-2 text-xs text-rose-900 dark:border-rose-900/60 dark:bg-rose-950/50 dark:text-rose-100">
              {affectedActiveTasks.map((taskRow) => (
                <li key={taskRow.taskId}>- {taskRow.title}</li>
              ))}
            </ul>
            </>
          ) : null}
        </div>
      </ConfirmDialog>
    </div>
  );
};
